import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  RightOutlined,
  SearchOutlined,
  FileSearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import {
  Container,
  Title,
  SubTitle,
  SearchContainer,
  InputWrapper,
  IconWrapper,
  StyledInput,
} from "../style";
import {
  AssociatedCredentialsTopItems,
  CredentialsTable,
  StyledTabs,
  TopContent,
  SearchBanner,
  SearchBannerIcon,
  SearchBannerContent,
  SearchBannerTitle,
  SearchBannerText,
  SearchBannerActions,
  SearchBannerButton,
  SearchBannerGhostButton,
  SkeletonCard,
  SkeletonShimmer,
  ClearSearchButton,
  HeaderLinks,
  HeaderLink,
} from "./styles";
import { StyledButton } from "./Collaborators/styles";
import CredentialsCard from "../../components/credentialsCard";
import { notification, Empty } from "antd";
import api from "../../../../service";
import { useSelector } from "react-redux";
import performanceSecurityIcon from "../../components/Icons/performance-security-icon.svg";
import providerIcon from "../../components/Icons/provider-icon.svg";
import assembleIcon from "../../components/Icons/assemble-icon.svg";
import exhibitorIcon from "../../components/Icons/exhibitor-icon.svg";
import assemblyDisassemblySecurityIcon from "../../components/Icons/assembly-disassembly-security-icon.svg";
import sponsorIcon from "../../components/Icons/sponsor-icon.svg";
import servicesIcon from "../../components/Icons/services-icon.svg";
import pressIcon from "../../components/Icons/press-icon.svg";
import advisoryIcon from "../../components/Icons/advisory-icon.svg";
import DropdownFilter from "../../components/credentialsDropFilter";
import DisassociateAndWithdrawal from "../../components/credentialsDisassociateAndWithdrawal";
import CredentialsPrintedTab from "../../components/CredentialsPrintedTab";
import { Nav } from "../Sales/styles";
import GoBackButton from "../../components/goBackButton";
import { FantasyNameAndContractBadge } from "../../components/badges";
import PrintProtocol from "../../components/credentialsPrintProtocol";
import ModalNewCollaborator from "../../../../components/ModalNewCollaborator";
import BonusCredentialModal from "../../components/BonusCredentialModal";
import { FaRegEdit, FaRegIdBadge } from "react-icons/fa";
import {
  SORT_FIELD,
  SORT_ORDER,
  loadSort,
  saveSort,
  toAntdSortOrder,
} from "../../../../constants/sortCredentialStaff";

const formatSortParams = (field, order) => {
  if (!field || !order) {
    return {};
  }
  return {
    sortBy: field,
    sortOrder: order.toUpperCase(),
  };
};

const pickRow = ({ id, collaboratorName, credentialType }) => ({
  id,
  collaboratorName,
  credentialType,
});

const SEARCHABLE_TABS = ["2", "3", "4"];

const SEARCH_TAB_STATES = [
  { tab: "2", isPrinted: false, withdrawn: false },
  { tab: "3", isPrinted: true, withdrawn: false },
  { tab: "4", isPrinted: true, withdrawn: true },
];

const normalize = (value) =>
  value
    ?.toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase() || "";

const itemMatchesTerm = (item, term) => {
  const normalizedTerm = normalize(term);
  if (!normalizedTerm) return false;
  return (
    normalize(item?.document).includes(normalizedTerm) ||
    normalize(item?.collaboratorName).includes(normalizedTerm)
  );
};

const SkeletonCardRow = () => (
  <SkeletonCard>
    <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
      <SkeletonShimmer $w="48px" $h="48px" $radius="50%" />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SkeletonShimmer $w="200px" $h="18px" />
        <SkeletonShimmer $w="130px" $h="14px" />
      </div>
    </div>
    <SkeletonShimmer $w="120px" $h="38px" $radius="8px" />
  </SkeletonCard>
);

const useSortState = (initialTab) => {
  // Guarda a aba corrente para persistir a ordenação no bucket certo do sessionStorage.
  const tabRef = useRef(initialTab);
  const [sortConfig, setSortConfig] = useState(() => loadSort(initialTab));

  const handleSort = useCallback((field) => {
    setSortConfig((prev) => {
      const next =
        !prev.field || prev.field !== field
          ? { field, order: SORT_ORDER.ASC }
          : {
              field,
              order:
                prev.order === SORT_ORDER.ASC
                  ? SORT_ORDER.DESC
                  : SORT_ORDER.ASC,
            };
      saveSort(tabRef.current, next);
      return next;
    });
  }, []);

  // Restaura a ordenação salva (ou o default da aba) ao trocar de aba/voltar para a tela.
  const restoreSortForTab = useCallback((tab) => {
    tabRef.current = tab;
    setSortConfig(loadSort(tab));
  }, []);

  return { sortConfig, handleSort, restoreSortForTab };
};

const StaffServiceCredentials = () => {
  const { token, permission } = useSelector((state) => state.auth);
  const intl = useIntl();
  const history = useHistory();

  // INFORMACP-4818: Staff precisa da permissao especifica; Admin entra pela permissao do
  // backoffice, independente da permissao de Staff.
  const canBonify =
    permission.includes("MANAGE_BONUS_CREDENTIALS_STAFF") ||
    permission.includes("MANAGE_BONUS_CREDENTIALS");

  // LocalStorage
  const fantasyName = localStorage.getItem("serviceFantasyname");
  const serviceContractNumber = localStorage.getItem("serviceContractNumber");
  const serviceOpportunityId = localStorage.getItem("serviceOpportunityId");
  const serviceEventId = localStorage.getItem("serviceEventId");
  const serviceSystemUserId = localStorage.getItem("serviceSystemUserId");

  // Estados principais
  const { search } = history.location;
  const queryTab = new URLSearchParams(search).get("tab");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(queryTab || "1");
  const activeTabRef = useRef("1");
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [credentialTransfers, setCredentialTransfers] = useState([]);
  // INFORMACP-4504: tipos de credencial visiveis para o tipo de contrato da oportunidade.
  // null enquanto a cota nao foi carregada — nao confundir com lista vazia.
  const [allowedCredentialTypes, setAllowedCredentialTypes] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [printRows, setPrintRows] = useState([]);
  const [printWithdrawRows, setPrintWithdrawRows] = useState([]);
  const [highlightedPrintedIds, setHighlightedPrintedIds] = useState([]);
  const [highlightedWithdrawnIds, setHighlightedWithdrawnIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingTable, setLoadingTable] = useState(false);
  const [associatedCredentials, setAssociatedCredentials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchIndex, setSearchIndex] = useState({
    loaded: false,
    data: { 2: [], 3: [], 4: [] },
  });
  const [searchStatus, setSearchStatus] = useState(null);
  const [redirectPulse, setRedirectPulse] = useState(false);
  const [bonusCredentialModalOpen, setBonusCredentialModalOpen] =
    useState(false);
  const searchIndexLoadingRef = useRef(false);
  const searchRequestRef = useRef(0);
  const pulseTimeoutRef = useRef(null);
  // Guarda o termo para o qual o redirecionamento automatico ja foi aplicado.
  // Garante que o auto-redirect ocorra apenas numa busca nova, permitindo que
  // o usuario troque de aba manualmente sem ser jogado de volta e sem perder a busca.
  const redirectedTermRef = useRef("");
  const [modalVisibleProtocol, setModalVisibleProtocol] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [companyNameCredential, setCompanyNameCredential] = useState("");
  const [editingCollaboratorId, setEditingCollaboratorId] = useState(null);
  const [isEditingCollaborator, setIsEditingCollaborator] = useState(false);
  const [modalEditCollaborator, setModalEditCollaborator] = useState(false);

  const { sortConfig, handleSort, restoreSortForTab } = useSortState(activeTab);

  // Controle de modais
  const [modalState, setModalState] = useState({
    isAssociateOpen: false,
    isTransferOpen: false,
    selectedCard: null,
  });

  const handleGoBack = () => {
    history.push("/staff-access/service-panel");
  };

  const handleEditCollaborator = (id) => {
    setEditingCollaboratorId(id);
    setIsEditingCollaborator(true);
    setModalEditCollaborator(true);
  };

  const handleOpenModal = (card) => {
    setModalState({
      isAssociateOpen: true,
      isTransferOpen: false,
      selectedCard: card,
    });
  };

  const handleOpenModalTransfer = (card) => {
    setModalState({
      isAssociateOpen: false,
      isTransferOpen: true,
      selectedCard: card,
    });
  };

  const handleCloseModals = () => {
    setModalState({
      isAssociateOpen: false,
      isTransferOpen: false,
      selectedCard: null,
    });
  };

  const handleTableChange = (_pagination, _filters, sorter, extra) => {
    // antd v4 dispara onChange tambem ao paginar; so reordena em acao de sort,
    // evitando que a troca de pagina reinverta a ordenacao vigente.
    if (extra?.action !== "sort") {
      return;
    }
    if (sorter?.field) {
      handleSort(sorter.field);
    }
  };

  const handleTabChange = useCallback(
    (newTab) => {
      activeTabRef.current = newTab;
      setActiveTab(newTab);
      restoreSortForTab(newTab);
      setCurrentPage(1);
      setPageSize(10);
      setSelectedCategory("ALL");
      // A busca NAO e limpada ao trocar de aba: o usuario deve poder navegar
      // entre Disponiveis/Impressas/Retiradas mantendo o termo pesquisado.
      setSelectedRows([]);
      setPrintRows([]);
      setPrintWithdrawRows([]);
    },
    [restoreSortForTab],
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleGoBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const anyModalWasOpenRef = useRef(false);

  useEffect(() => {
    fetchCredentialsCount();
    invalidateSearchIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const anyModalOpen =
      modalState.isAssociateOpen || modalState.isTransferOpen;
    const justClosed = anyModalWasOpenRef.current && !anyModalOpen;
    anyModalWasOpenRef.current = anyModalOpen;

    if (justClosed) {
      fetchCredentialsCount({ silent: true });
      invalidateSearchIndex();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.isAssociateOpen, modalState.isTransferOpen]);

  useEffect(() => {
    const { tabKey } = location.state || {};
    if (tabKey) {
      setActiveTab(tabKey);
      restoreSortForTab(tabKey);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "1") {
      fetchCredentialsInfos();
    }
  }, [activeTab, sortConfig]);

  // as credenciais recem-impressas. A marcacao e temporaria (some ao sair da tela).
  const handlePrintedNow = useCallback(
    (printedIds) => {
      if (!printedIds || printedIds.length === 0) return;
      setHighlightedPrintedIds(printedIds);
      activeTabRef.current = "3";
      setActiveTab("3");
      restoreSortForTab("3");
    },
    [restoreSortForTab],
  );

  const handleAssociatedPrintLater = useCallback(() => {
    activeTabRef.current = "2";
    setActiveTab("2");
  }, []);

  // Pre-seleciona as linhas recem-impressas assim que a lista da aba Impressas carrega.
  useEffect(() => {
    if (activeTab !== "3" || highlightedPrintedIds.length === 0) return;
    const rows = (associatedCredentials || [])
      .filter((c) => highlightedPrintedIds.includes(c.id))
      .map(({ id, collaboratorName, credentialType }) => ({
        id,
        collaboratorName,
        credentialType,
      }));
    if (rows.length) setPrintWithdrawRows(rows);
  }, [activeTab, associatedCredentials, highlightedPrintedIds]);

  // Ao sair da aba Impressas, volta ao estado padrao (marcacao + selecao temporarias).
  useEffect(() => {
    if (activeTab !== "3") {
      setHighlightedPrintedIds([]);
      setPrintWithdrawRows([]);
    }
  }, [activeTab]);

  const handleWithdrawnNow = useCallback(
    (withdrawnIds) => {
      if (!withdrawnIds || withdrawnIds.length === 0) return;
      setHighlightedWithdrawnIds(withdrawnIds);
      activeTabRef.current = "4";
      setActiveTab("4");
      restoreSortForTab("4");
    },
    [restoreSortForTab],
  );

  // Pre-seleciona as linhas recem-retiradas assim que a lista da aba Retiradas carrega.
  useEffect(() => {
    if (activeTab !== "4" || highlightedWithdrawnIds.length === 0) return;
    const rows = (associatedCredentials || [])
      .filter((c) => highlightedWithdrawnIds.includes(c.id))
      .map(({ id, collaboratorName, credentialType }) => ({
        id,
        collaboratorName,
        credentialType,
      }));
    if (rows.length) setPrintRows(rows);
  }, [activeTab, associatedCredentials, highlightedWithdrawnIds]);

  // Ao sair da aba Retiradas, limpa a marcacao temporaria.
  useEffect(() => {
    if (activeTab !== "4") {
      setHighlightedWithdrawnIds([]);
    }
  }, [activeTab]);

  // Mantem a ref do tab sincronizada (seguranca para qualquer troca de aba).
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const fetchCredentialsCount = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const result = await api.get(`/accreditation/quota`, {
          params: {
            opportunity_id: serviceOpportunityId,
            event_id: serviceEventId,
            user_id: serviceSystemUserId,
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        });

        setCredentials(result?.data?.credentialCategoryOutputList || []);
        setCredentialTransfers(
          result?.data?.credentialTransferCategoryOutputList || []
        );
        setAllowedCredentialTypes(
          result?.data?.allowedCredentialTypeList || []
        );
        setCompanyNameCredential(result?.data?.companyNameCredential || "");
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: `api.error.${error.response?.data?.description || "unknown"}`,
          }),
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [token, serviceOpportunityId, serviceEventId, serviceSystemUserId, intl]
  );

  // A bonificacao e vinculada ao atendimento: sem o staff_service criado no painel nao ha
  // contexto para gravar, entao o modal nao abre com um id nulo.
  const openBonusCredentialModal = useCallback(() => {
    // Um serviceId ausente ou corrompido viraria NaN no payload e o usuario receberia o erro
    // generico de validacao em vez da mensagem que explica o que fazer. Number(null) e Number("")
    // valem 0, entao a checagem de positivo cobre os dois casos.
    const staffServiceId = Number(localStorage.getItem("serviceId"));

    if (!Number.isInteger(staffServiceId) || staffServiceId <= 0) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "bonus_credential_without_attendance",
        }),
      });
      return;
    }

    setBonusCredentialModalOpen(true);
  }, [intl]);

  const fetchCredentialsInfos = useCallback(async () => {
    setLoadingTable(true);
    try {
      const sortParams = formatSortParams(sortConfig.field, sortConfig.order);

      const result = await api.patch(
        `/admin/accreditation/`,
        {
          eventId: serviceEventId,
          systemUserId: serviceSystemUserId,
          opportunityId: serviceOpportunityId,
          isPrinted: activeTabRef.current !== "2",
          withdrawn: activeTabRef.current === "4",
          ...sortParams,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setAssociatedCredentials(result?.data?.credentialAdminOutputList || []);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: `api.error.${error.response?.data?.description || "unknown"}`,
        }),
      });
    } finally {
      setLoadingTable(false);
    }
  }, [
    sortConfig,
    token,
    serviceEventId,
    serviceSystemUserId,
    serviceOpportunityId,
    intl,
  ]);

  const fetchSearchIndex = useCallback(async () => {
    if (searchIndexLoadingRef.current) return;
    searchIndexLoadingRef.current = true;
    try {
      const responses = await Promise.all(
        SEARCH_TAB_STATES.map((tabState) =>
          api
            .patch(
              `/admin/accreditation/`,
              {
                eventId: serviceEventId,
                systemUserId: serviceSystemUserId,
                opportunityId: serviceOpportunityId,
                isPrinted: tabState.isPrinted,
                withdrawn: tabState.withdrawn,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then((result) => ({
              tab: tabState.tab,
              list: result?.data?.credentialAdminOutputList || [],
            }))
            .catch(() => ({ tab: tabState.tab, list: [] }))
        )
      );

      const data = { 2: [], 3: [], 4: [] };
      responses.forEach(({ tab, list }) => {
        data[tab] = list;
      });
      setSearchIndex({ loaded: true, data });
    } finally {
      searchIndexLoadingRef.current = false;
    }
  }, [serviceEventId, serviceSystemUserId, serviceOpportunityId, token]);

  const invalidateSearchIndex = useCallback(() => {
    setSearchIndex((prev) => (prev.loaded ? { ...prev, loaded: false } : prev));
  }, []);

  const handleDataRefresh = useCallback(() => {
    fetchCredentialsInfos();
    fetchCredentialsCount();
    invalidateSearchIndex();
  }, [fetchCredentialsInfos, fetchCredentialsCount, invalidateSearchIndex]);

  const filteredData = useMemo(
    () =>
      associatedCredentials?.filter((item) => {
        const matchesCategory =
          selectedCategory === "ALL" ||
          item.credentialType === selectedCategory;

        const normalizedSearch = normalize(searchTerm);
        const matchesName = normalize(item.collaboratorName).includes(
          normalizedSearch,
        );
        const matchesDocument = normalize(item.document).includes(
          normalizedSearch,
        );

        return matchesCategory && (matchesName || matchesDocument);
      }) || [],
    [associatedCredentials, selectedCategory, searchTerm],
  );

  const triggerRedirectPulse = useCallback(() => {
    setRedirectPulse(true);
    if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    pulseTimeoutRef.current = setTimeout(() => setRedirectPulse(false), 1300);
  }, []);

  const lookupCollaborator = useCallback(
    async (term, requestId) => {
      try {
        const result = await api.get(`/associate-collaborators`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: 0,
            per_page: 1,
            search: term,
            userId: serviceSystemUserId,
            opportunity_id: serviceOpportunityId,
          },
        });
        if (requestId !== searchRequestRef.current) return;
        const list = result?.data?.associateCollaboratorListOutput || [];
        setSearchStatus(list.length > 0 ? "collaborator_only" : "not_found");
      } catch (error) {
        if (requestId !== searchRequestRef.current) return;
        setSearchStatus("not_found");
      }
    },
    [token, serviceSystemUserId, serviceOpportunityId]
  );

  const matchingTabs = useMemo(() => {
    const term = searchTerm.trim();
    if (!term || !searchIndex.loaded) return null;
    return SEARCHABLE_TABS.filter((tab) =>
      (searchIndex.data[tab] || []).some((item) => itemMatchesTerm(item, term))
    );
  }, [searchTerm, searchIndex]);

  useEffect(() => {
    const term = searchTerm.trim();

    if (!term) {
      searchRequestRef.current += 1;
      setSearchStatus(null);
      return;
    }

    const handler = setTimeout(() => {
      const requestId = ++searchRequestRef.current;

      if (!searchIndex.loaded) {
        fetchSearchIndex();
        return;
      }

      const anyMatch = SEARCHABLE_TABS.some((tab) =>
        (searchIndex.data[tab] || []).some((item) =>
          itemMatchesTerm(item, term)
        )
      );

      if (anyMatch) {
        setSearchStatus(null);
        return;
      }

      setSearchStatus("loading");
      lookupCollaborator(term, requestId);
    }, 350);

    return () => clearTimeout(handler);
  }, [searchTerm, searchIndex, fetchSearchIndex, lookupCollaborator]);

  // Redireciona para a PRIMEIRA aba com resultado. Numa busca nova sempre leva a
  // primeira aba correspondente; depois, como so as abas com resultado ficam
  // visiveis, o usuario navega entre elas mantendo o termo, sem cair em aba vazia.
  useEffect(() => {
    const term = searchTerm.trim();

    if (!term) {
      redirectedTermRef.current = "";
      return;
    }

    if (!matchingTabs || matchingTabs.length === 0) return;

    // Se o usuario foi para uma aba nao pesquisavel (ex.: Disponiveis), deixa-o
    // navegar livremente para fora da busca — nao força retorno a aba de resultado.
    if (!SEARCHABLE_TABS.includes(activeTab)) return;

    const target = matchingTabs[0];
    const isNewSearch = term !== redirectedTermRef.current;
    const activeIsMatching = matchingTabs.includes(activeTab);
    redirectedTermRef.current = term;

    if (!activeIsMatching || (isNewSearch && activeTab !== target)) {
      activeTabRef.current = target;
      setActiveTab(target);
      restoreSortForTab(target);
      setCurrentPage(1);
      triggerRedirectPulse();
    }
  }, [
    searchTerm,
    matchingTabs,
    activeTab,
    restoreSortForTab,
    triggerRedirectPulse,
  ]);

  const tabs = [
    { key: "1", label: intl.formatMessage({ id: "available" }) },
    { key: "2", label: intl.formatMessage({ id: "associated2" }) },
    { key: "3", label: intl.formatMessage({ id: "printed_plural" }) },
    { key: "4", label: intl.formatMessage({ id: "withdrawns" }) },
  ];

  // Enquanto ha resultado em uma ou duas abas pesquisaveis, exibe apenas as
  // que tem credencial correspondente; caso o termo apareca em todas (ou nao
  // haja busca ativa / nada localizado), mantem todas visiveis. A aba
  // Disponiveis (nao pesquisavel) permanece sempre visivel.
  const visibleTabs =
    matchingTabs && matchingTabs.length > 0
      ? tabs.filter(
          (tab) =>
            !SEARCHABLE_TABS.includes(tab.key) || matchingTabs.includes(tab.key)
        )
      : tabs;

  const columnsAssociated = [
    {
      title: intl.formatMessage({ id: "associated_in" }),
      dataIndex: "associatedAt",
      width: "10%",
      key: "associatedAt",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.ASSOCIATED_AT
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (associatedAt) =>
        new Intl.DateTimeFormat("pt-BR").format(new Date(associatedAt)),
    },
    {
      title: intl.formatMessage({ id: "category" }),
      dataIndex: "credentialType",
      width: "12%",
      key: "credentialType",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_TYPE
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (credentialType) =>
        intl.formatMessage({ id: credentialType || "-" }),
    },
    {
      title: intl.formatMessage({ id: "collaborator_name" }),
      dataIndex: "collaboratorName",
      width: "16%",
      key: "collaboratorName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.COLLABORATOR_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "credential_name" }),
      dataIndex: "credentialName",
      width: "16%",
      key: "credentialName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (credentialName) => <span>{credentialName ?? "-"}</span>,
    },
    {
      title: intl.formatMessage({ id: "document" }),
      dataIndex: "document",
      width: "11%",
      key: "document",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.DOCUMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "assignment" }),
      dataIndex: "assignment",
      width: "12%",
      key: "assignment",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.ASSIGNMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (assignment) => <span>{assignment ?? "-"}</span>,
    },
    {
      title: intl.formatMessage({ id: "company_name_credential" }),
      dataIndex: "companyName",
      width: "18%",
      key: "companyName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.COMPANY_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "edit" }),
      dataIndex: "editCollaborator",
      width: "5%",
      key: "editCollaborator",
      sorter: false,
      render: (_, record) => (
        <StyledButton
          icon={<FaRegEdit size={22} />}
          onClick={() => handleEditCollaborator(record.collaboratorId)}
        />
      ),
    },
  ];

  const columnsPrinted = [
    {
      title: intl.formatMessage({ id: "category" }),
      dataIndex: "credentialType",
      width: "16%",
      key: "credentialType",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_TYPE
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (credentialType) =>
        intl.formatMessage({ id: credentialType || "-" }),
    },
    {
      title: intl.formatMessage({ id: "collaborator_name" }),
      dataIndex: "collaboratorName",
      width: "21%",
      key: "collaboratorName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.COLLABORATOR_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "credential_name" }),
      dataIndex: "credentialName",
      width: "21%",
      key: "credentialName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (credentialName) => <span>{credentialName ?? "-"}</span>,
    },
    {
      title: intl.formatMessage({ id: "document" }),
      dataIndex: "document",
      width: "14%",
      key: "document",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.DOCUMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "assignment" }),
      dataIndex: "assignment",
      width: "14%",
      key: "assignment",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.ASSIGNMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (assignment) => <span>{assignment ?? "-"}</span>,
    },
    {
      title: intl.formatMessage({ id: "company_name_credential" }),
      dataIndex: "companyName",
      width: "14%",
      key: "companyName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.COMPANY_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
  ];

  const columnsWithdrawn = [
    {
      title: intl.formatMessage({ id: "withdrawal_date_" }),
      dataIndex: "withdrawnAt",
      width: "12%",
      key: "withdrawnAt",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.WITHDRAWN_AT
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (withdrawnAt) =>
        withdrawnAt
          ? new Intl.DateTimeFormat("pt-BR").format(new Date(withdrawnAt))
          : "-",
    },
    {
      title: intl.formatMessage({ id: "category" }),
      dataIndex: "credentialType",
      width: "12%",
      key: "credentialType",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_TYPE
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (credentialType) =>
        intl.formatMessage({ id: credentialType || "-" }),
    },
    {
      title: intl.formatMessage({ id: "credential_name" }),
      dataIndex: "credentialName",
      width: "18%",
      key: "credentialName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.CREDENTIAL_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "document" }),
      dataIndex: "document",
      width: "12%",
      key: "document",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.DOCUMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "assignment" }),
      dataIndex: "assignment",
      width: "12%",
      key: "assignment",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.ASSIGNMENT
          ? toAntdSortOrder(sortConfig.order)
          : null,
      render: (assignment) => <span>{assignment ?? "-"}</span>,
    },
    {
      title: intl.formatMessage({ id: "company_name_credential" }),
      dataIndex: "companyName",
      width: "17%",
      key: "companyName",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.COMPANY_NAME
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "withdrawn_by" }),
      dataIndex: "nameOfSignature",
      width: "10%",
      key: "nameOfSignature",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.NAME_OF_SIGNATURE
          ? toAntdSortOrder(sortConfig.order)
          : null,
    },
    {
      title: intl.formatMessage({ id: "withdrawn_protocol" }),
      dataIndex: "protocol",
      width: "12%",
      key: "protocol",
      sorter: true,
      sortOrder:
        sortConfig.field === SORT_FIELD.PROTOCOL
          ? toAntdSortOrder(sortConfig.order)
          : null,
      onCell: (record) => ({
        onClick: () => {
          setSelectedProtocol(record);
          setModalVisibleProtocol(true);
        },
      }),
      render: (protocol) => (
        <span
          style={{
            fontWeight: "bold",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {String(protocol || "").padStart(4, "0")}
        </span>
      ),
    },
    {
      title: intl.formatMessage({ id: "has_duplicate" }),
      dataIndex: "hasDuplicate",
      width: "10%",
      key: "hasDuplicate",
      render: (hasDuplicate) =>
        intl.formatMessage({ id: hasDuplicate ? "yes" : "no" }),
    },
  ];

  const credentialsData = [
    {
      key: "exhibitor",
      credentialType: "EXHIBITOR",
      icon: exhibitorIcon,
    },
    {
      key: "provider",
      credentialType: "PROVIDER",
      icon: providerIcon,
    },
    {
      key: "performance_security",
      credentialType: "PERFORMANCE_SECURITY",
      icon: performanceSecurityIcon,
    },
    {
      key: "assemble",
      credentialType: "ASSEMBLE",
      icon: assembleIcon,
    },
    {
      key: "assembly_disassembly_security",
      credentialType: "ASSEMBLY_DISASSEMBLY_SECURITY",
      icon: assemblyDisassemblySecurityIcon,
    },
    {
      key: "sponsorship",
      credentialType: "SPONSOR",
      icon: sponsorIcon,
    },
    {
      key: "services",
      credentialType: "SERVICES",
      icon: servicesIcon,
    },
    {
      key: "press",
      credentialType: "PRESS",
      icon: pressIcon,
    },
    {
      key: "advisory",
      credentialType: "ADVISORY",
      icon: advisoryIcon,
    },
  ]

    .filter((item) =>
      allowedCredentialTypes
        ? allowedCredentialTypes.includes(item.credentialType)
        : false
    )
    .map((item) => {
      const associateData = credentials?.find(
        (cred) => cred.credentialType === item.credentialType
      );
      const transferData = credentialTransfers?.find(
        (transf) => transf.credentialType === item.credentialType
      );
      return {
        ...item,
        associateData,
        transferData,
        associateAvailable: associateData?.available || 0,
        transferAvailable: transferData?.available || 0,
      };
    });

  const searchTermTrimmed = searchTerm.trim();

  const isContractEmptyForTab =
    SEARCHABLE_TABS.includes(activeTab) &&
    !loadingTable &&
    (associatedCredentials?.length ?? 0) === 0;

  const cardsLoading = loading;

  const tableEmptyDescription =
    searchTermTrimmed && SEARCHABLE_TABS.includes(activeTab)
      ? searchStatus === "collaborator_only"
        ? intl.formatMessage(
            { id: "search_table.no_credential_yet" },
            {
              term: searchTermTrimmed,
              verb: intl.formatMessage({
                id:
                  activeTab === "2"
                    ? "credential_verb_associated"
                    : activeTab === "3"
                    ? "credential_verb_printed"
                    : "credential_verb_withdrawn",
              }),
            }
          )
        : intl.formatMessage(
            { id: "search_table.no_result" },
            { term: searchTermTrimmed }
          )
      : null;

  const tableEmpty = tableEmptyDescription ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={tableEmptyDescription}
    />
  ) : undefined;

  const renderTabContent = () => {
    switch (activeTab) {
      case "1":
        return (
          <div style={{ gap: "10px" }}>
            {cardsLoading
              ? Array.from({ length: 5 }).map((_, skeletonIndex) => (
                  <SkeletonCardRow key={skeletonIndex} />
                ))
              : credentialsData.map((card) => {
                  const formattedType = intl.formatMessage({ id: card.key });

                  const isSelectedCard =
                    modalState.selectedCard?.key === card.key;

                  return (
                    <CredentialsCard
                      key={card.key}
                      noTransfer={card.transferAvailable < 1}
                      associateQuantity={card.associateAvailable || 0}
                      transferQuantity={card.transferAvailable || 0}
                      type={formattedType}
                      selectedQuantity={card.transferAvailable}
                      needAcquire={card.associateAvailable === 0}
                      icon={card.icon}
                      credential={card.credentialType}
                      modalType={formattedType}
                      modalIcon={card.icon}
                      isModalVisible={
                        modalState.isAssociateOpen && isSelectedCard
                      }
                      isModalVisibleTransfer={
                        modalState.isTransferOpen && isSelectedCard
                      }
                      onCloseModal={handleCloseModals}
                      onCloseModalTransfer={handleCloseModals}
                      onPrintedNow={handlePrintedNow}
                      onPrintLater={handleAssociatedPrintLater}
                      onOpenModal={() =>
                        handleOpenModal({
                          ...card,
                          formattedType,
                        })
                      }
                      onOpenModalTransfer={() =>
                        handleOpenModalTransfer({
                          ...card,
                          formattedType,
                        })
                      }
                    />
                  );
                })}
          </div>
        );

      case "2":
        return (
          <>
            {isEditingCollaborator && (
              <ModalNewCollaborator
                modalVisibleDelete={false}
                setModalVisible={setModalEditCollaborator}
                visible={modalEditCollaborator}
                editingId={editingCollaboratorId}
                setEditingId={setEditingCollaboratorId}
                setIsEditing={setIsEditingCollaborator}
                isEditing={isEditingCollaborator}
                selectedRows={[]}
                fetchData={handleDataRefresh}
              />
            )}

            <AssociatedCredentialsTopItems>
              <DropdownFilter
                onSelect={setSelectedCategory}
                allowedTypes={allowedCredentialTypes}
              />
              <DisassociateAndWithdrawal
                fetchData={handleDataRefresh}
                selectedRowKeys={selectedRows}
                setSelectedRows={setSelectedRows}
                companyNameCredential={companyNameCredential}
                onWithdrawnNow={handleWithdrawnNow}
                onPrintedNow={handlePrintedNow}
              />
            </AssociatedCredentialsTopItems>

            <CredentialsTable
              rowKey="id"
              columns={columnsAssociated}
              loading={loadingTable}
              onChange={handleTableChange}
              rowSelection={{
                selectedRowKeys: selectedRows.map((row) => row.id),
                onSelect: (record, selected) =>
                  setSelectedRows((prev) =>
                    selected
                      ? [...prev, pickRow(record)]
                      : prev.filter((row) => row.id !== record.id)
                  ),
                // Seleciona/limpa TODAS as linhas de todas as páginas (dados
                // paginados no front), não só as visíveis na página atual.
                onSelectAll: (selected) =>
                  setSelectedRows(selected ? filteredData.map(pickRow) : []),
              }}
              dataSource={filteredData}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "15", "20", "25", "30"],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPageSize(pageSize);
                },
                showTotal: (total, range) =>
                  `${intl.formatMessage({ id: "displaying" })} ${range[0]} - ${
                    range[1]
                  } ${intl.formatMessage({ id: "of" })} ${total}`,
                locale: { items_per_page: "", emptyText: tableEmpty },
                showLessItems: true,
                showQuickJumper: false,
              }}
            />
          </>
        );

      case "3":
        return (
          <>
            <AssociatedCredentialsTopItems>
              <DropdownFilter
                onSelect={setSelectedCategory}
                allowedTypes={allowedCredentialTypes}
              />
              <CredentialsPrintedTab
                fetchData={handleDataRefresh}
                selectedRowKeys={printWithdrawRows}
                setSelectedRows={setPrintWithdrawRows}
                companyNameCredential={companyNameCredential}
                sortedData={filteredData}
                onWithdrawnNow={handleWithdrawnNow}
              />
            </AssociatedCredentialsTopItems>

            <CredentialsTable
              rowKey="id"
              columns={columnsPrinted}
              loading={loadingTable}
              onChange={handleTableChange}
              rowClassName={(record) =>
                highlightedPrintedIds.includes(record.id)
                  ? "highlighted-printed-row"
                  : ""
              }
              rowSelection={{
                selectedRowKeys: printWithdrawRows.map((row) => row.id),
                onSelect: (record, selected) =>
                  setPrintWithdrawRows((prev) =>
                    selected
                      ? [...prev, pickRow(record)]
                      : prev.filter((row) => row.id !== record.id)
                  ),

                onSelectAll: (selected) =>
                  setPrintWithdrawRows(
                    selected ? filteredData.map(pickRow) : []
                  ),
              }}
              dataSource={filteredData}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "15", "20", "25", "30"],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPageSize(pageSize);
                },
                showTotal: (total, range) =>
                  `${intl.formatMessage({ id: "displaying" })} ${range[0]} - ${
                    range[1]
                  } ${intl.formatMessage({ id: "of" })} ${total}`,
                locale: { items_per_page: "", emptyText: tableEmpty },
                showLessItems: true,
                showQuickJumper: false,
              }}
            />
          </>
        );

      case "4":
        return (
          <>
            <AssociatedCredentialsTopItems>
              <DropdownFilter
                onSelect={setSelectedCategory}
                allowedTypes={allowedCredentialTypes}
              />
              <PrintProtocol
                data={selectedProtocol}
                visible={modalVisibleProtocol}
                setVisible={setModalVisibleProtocol}
                fetchData={handleDataRefresh}
                selectedRowKeys={printRows}
                setSelectedRows={setPrintRows}
                companyNameCredential={companyNameCredential}
                credentialTypes={credentialsData}
                sortedData={filteredData}
              />
            </AssociatedCredentialsTopItems>

            <CredentialsTable
              rowKey="id"
              columns={columnsWithdrawn}
              loading={loadingTable}
              onChange={handleTableChange}
              rowClassName={(record) =>
                highlightedWithdrawnIds.includes(record.id)
                  ? "highlighted-withdrawn-row"
                  : ""
              }
              rowSelection={{
                selectedRowKeys: printRows.map((row) => row.id),
                onSelect: (record, selected) =>
                  setPrintRows((prev) =>
                    selected
                      ? [...prev, pickRow(record)]
                      : prev.filter((row) => row.id !== record.id)
                  ),
                // Seleciona/limpa TODAS as linhas de todas as páginas.
                onSelectAll: (selected) =>
                  setPrintRows(selected ? filteredData.map(pickRow) : []),
              }}
              dataSource={filteredData}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "15", "20", "25", "30"],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPageSize(pageSize);
                },
                showTotal: (total, range) =>
                  `${intl.formatMessage({ id: "displaying" })} ${range[0]} - ${
                    range[1]
                  } ${intl.formatMessage({ id: "of" })} ${total}`,
                locale: { items_per_page: "", emptyText: tableEmpty },
                showLessItems: true,
                showQuickJumper: false,
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <Nav>
        <GoBackButton
          noPosition
          href={"/staff-access/service-panel"}
          intlId="backToDashboard"
        />

        <FantasyNameAndContractBadge
          fantasyname={fantasyName}
          contractNumber={serviceContractNumber}
        />
      </Nav>

      <TopContent>
        <Title>{intl.formatMessage({ id: "credentials" })} </Title>
        <HeaderLinks>
          <HeaderLink
            onClick={() =>
              history.push("/staff-access/credentials/collaborators")
            }
          >
            <SubTitle>
              {intl.formatMessage({ id: "list_of_Contributors" })}
            </SubTitle>

            <RightOutlined />
          </HeaderLink>

          {canBonify && (
            <HeaderLink onClick={openBonusCredentialModal}>
              <FaRegIdBadge />
              <SubTitle>{intl.formatMessage({ id: "bonification" })}</SubTitle>

              <RightOutlined />
            </HeaderLink>
          )}
        </HeaderLinks>
      </TopContent>

      {canBonify && (
        <BonusCredentialModal
          open={bonusCredentialModalOpen}
          setOpen={setBonusCredentialModalOpen}
          allowedCredentialTypes={allowedCredentialTypes}
          onSuccess={fetchCredentialsCount}
        />
      )}

      {activeTab !== "1" && (
        <SearchContainer style={{ marginBottom: "20px" }}>
          <InputWrapper>
            <IconWrapper>
              <SearchOutlined />
            </IconWrapper>
            <StyledInput
              style={{ paddingLeft: "30px", paddingRight: "38px" }}
              placeholder={intl.formatMessage({
                id: "search",
              })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearSearchButton
                type="button"
                aria-label={intl.formatMessage({ id: "clear_fields" })}
                title={intl.formatMessage({ id: "clear_fields" })}
                onClick={() => setSearchTerm("")}
              >
                <CloseOutlined />
              </ClearSearchButton>
            )}
          </InputWrapper>
        </SearchContainer>
      )}

      {activeTab !== "1" &&
        (() => {
          if (isContractEmptyForTab) {
            return (
              <SearchBanner role="alert">
                <SearchBannerIcon>
                  <FileSearchOutlined />
                </SearchBannerIcon>
                <SearchBannerContent>
                  <SearchBannerTitle>
                    {intl.formatMessage({
                      id: "search_banner.empty_contract_title",
                    })}
                  </SearchBannerTitle>
                  <SearchBannerText>
                    {intl.formatMessage({
                      id: "search_banner.empty_contract_text",
                    })}
                  </SearchBannerText>
                  <SearchBannerActions>
                    <SearchBannerButton
                      onClick={() =>
                        history.push("/staff-access/credentials/collaborators")
                      }
                    >
                      {intl.formatMessage({
                        id: "search_banner.go_to_collaborators",
                      })}
                    </SearchBannerButton>
                    <SearchBannerGhostButton
                      onClick={() =>
                        history.push("/staff-access/services/sales")
                      }
                    >
                      {intl.formatMessage({
                        id: "search_banner.buy_credentials",
                      })}
                    </SearchBannerGhostButton>
                  </SearchBannerActions>
                </SearchBannerContent>
              </SearchBanner>
            );
          }

          if (!searchTermTrimmed) return null;

          if (searchStatus === "loading") {
            return (
              <SearchBanner>
                <SearchBannerIcon>
                  <SkeletonShimmer $w="20px" $h="20px" $radius="4px" />
                </SearchBannerIcon>
                <SearchBannerContent style={{ flex: 1 }}>
                  <SkeletonShimmer $w="220px" $h="16px" />
                  <SkeletonShimmer $w="320px" $h="14px" />
                </SearchBannerContent>
              </SearchBanner>
            );
          }

          if (searchStatus === "collaborator_only") {
            return (
              <SearchBanner role="alert">
                <SearchBannerIcon>
                  <FileSearchOutlined />
                </SearchBannerIcon>
                <SearchBannerContent>
                  <SearchBannerTitle>
                    {intl.formatMessage({
                      id: "search_banner.collaborator_only_title",
                    })}
                  </SearchBannerTitle>
                  <SearchBannerText>
                    {intl.formatMessage(
                      { id: "search_banner.collaborator_only_text" },
                      { term: <strong>{searchTermTrimmed}</strong> }
                    )}
                  </SearchBannerText>
                  <SearchBannerActions>
                    <SearchBannerButton
                      onClick={() =>
                        history.push("/staff-access/credentials/collaborators")
                      }
                    >
                      {intl.formatMessage({
                        id: "search_banner.go_to_collaborators",
                      })}
                    </SearchBannerButton>
                  </SearchBannerActions>
                </SearchBannerContent>
              </SearchBanner>
            );
          }

          if (searchStatus === "not_found") {
            return (
              <SearchBanner role="alert">
                <SearchBannerIcon>
                  <FileSearchOutlined />
                </SearchBannerIcon>
                <SearchBannerContent>
                  <SearchBannerTitle>
                    {intl.formatMessage({
                      id: "search_banner.not_found_title",
                    })}
                  </SearchBannerTitle>
                  <SearchBannerText>
                    {intl.formatMessage(
                      { id: "search_banner.not_found_text" },
                      { term: <strong>{searchTermTrimmed}</strong> }
                    )}
                  </SearchBannerText>
                </SearchBannerContent>
              </SearchBanner>
            );
          }

          return null;
        })()}

      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={visibleTabs}
        $glow={redirectPulse}
      />

      <div>{renderTabContent()}</div>
    </Container>
  );
};

export default StaffServiceCredentials;
